<script>
  // TODO:
  // A dot plot?
  // Spike plot?
  // On hover, shows a bar / line plot
  import data from "./data/seats_by_women.json";
  import topoJson from "./data/countries-50m.json";
  import {
    sort,
    ascending,
    range,
    min,
    max,
    group,
    rollup,
    geoEqualEarth,
    geoPath,
    scaleLinear,
    scaleSequential,
    least,
    line as d3line,
    area,
    curveMonotoneX,
    ticks,
    interpolateViridis,
  } from "d3";
  import { feature as topojsonFeature } from "topojson-client";
  import { fade } from "svelte/transition";
  import Scrubber from "./Scrubber.svelte";
  import Line from "./Line.svelte";
  import { countriesRename } from "./countries_rename";
  import { getGuid } from "./util.js";

  // Define chart dimensions
  let width;
  let height;

  const margins = {
    marginTop: 20,
    marginRight: 50,
    marginBottom: 50,
    marginLeft: 50,
  };
  // Chart dimensions will change upon resizing
  $: dimensions = {
    width,
    height,
    ...margins,
    boundedHeight: Math.max(
      height - margins.marginTop - margins.marginBottom,
      0
    ),
    boundedWidth: Math.max(width - margins.marginLeft - margins.marginRight, 0),
  };

  // Define state variables
  let selectedYear = 1997;
  let hoveredId = null;
  $: hoveredData = dataGrouped.get(hoveredId);

  // Define accessors
  const idAccessor = (d) => d["Country Name"];
  const yearAccessor = (d) => d["Year"];
  const positionAccessor = (d) => d.position;
  const lengthAccessor = (d) => d["Value"];

  // Define spikes
  const spike = (length, width = 7) =>
    `M${-width / 2},0L0,${-length}L${width / 2},0`;

  // Extract geo features
  const features = topojsonFeature(topoJson, topoJson.objects.countries);

  // Process data
  const idsToExclude = ["Tuvalu", "World"];
  let dataProcessed = data.filter(
    (d) => !idsToExclude.includes(d["Country Name"])
  );
  // Rename countries to match geo features
  dataProcessed = dataProcessed.map((d) => ({
    ...d,
    "Country Name": countriesRename.has(d["Country Name"])
      ? countriesRename.get(d["Country Name"])
      : d["Country Name"],
  }));

  let dataGrouped = rollup(
    dataProcessed,
    (g) => g[0],
    idAccessor,
    yearAccessor
  );

  // Define scales for small multiples
  const multipleSize = 100;

  const xAccessor = (d) => d["Year"];
  const xMin = min(dataProcessed, xAccessor);
  const xMax = max(dataProcessed, xAccessor);

  $: xScale = scaleLinear().domain([xMin, xMax]).range([0, multipleSize]);
  $: xAccessorScaled = (d) => xScale(xAccessor(d));

  const yAccessor = (d) => d["Value"];
  const yMin = min(dataProcessed, yAccessor);
  // const yMax = max(dataFiltered, yAccessor) + 5;
  const yMax = 50;
  $: yScale = scaleLinear().domain([yMin, yMax]).range([multipleSize, 0]);
  $: yAccessorScaled = (d) => yScale(yAccessor(d));

  // Define small multiple generators
  $: lineGenerator = d3line()
    .x(xAccessorScaled)
    .y(yAccessorScaled)
    .curve(curveMonotoneX)
    .defined((d) => yAccessorScaled(d));
  $: areaGenerator = area()
    .x(xAccessorScaled)
    .y0(yScale(0))
    .y1(yAccessorScaled)
    .curve(curveMonotoneX)
    .defined((d) => yAccessorScaled(d));

  // Define scales for spikes
  // const lengthMin = min(dataProcessed, lengthAccessor);
  // const lengthMax = max(dataProcessed, lengthAccessor);
  $: lengthRange = yScale.range().reverse();
  $: lengthScale = scaleLinear().domain([yMin, yMax]).range(lengthRange);
  $: lengthAccessorScaled = (d) => lengthScale(lengthAccessor(d));

  // Define color gradient
  const gradientId = getGuid();
  const colorAccessor = (d) => d["Value"];
  const colorScale = scaleSequential([0, 100], interpolateViridis);
  const colorAccessorScaled = (d) => colorScale(colorAccessor(d));

  // Process data for map
  const outline = { type: "Sphere" };
  let projection = geoEqualEarth();

  // Resizable map
  let mapHeight;
  $: {
    // Projection function
    // scale for [longitude, latitude] -> [x, y] pixels
    projection = projection.fitWidth(dimensions.boundedWidth, outline);

    // Get pixel dimensions of map based on the outline
    const [[x0, y0], [x1, y1]] = geoPath(projection).bounds(outline);
    mapHeight = y1 - y0;
  }

  $: pathGenerator = geoPath(projection);

  // Compute centroid for each geo feature
  const idToFeature = new Map(
    features.features.map((d) => [d.properties.name, d])
  );
  let dataRender;
  $: {
    dataGrouped.forEach((d, id) => {
      d.position = pathGenerator.centroid(idToFeature.get(id));
    });

    // Sort data points
    dataRender = sort(
      [...dataGrouped],
      ([x, a], [y, b]) =>
        ascending(a.position[1], b.position[1]) ||
        ascending(a.position[0], b.position[0])
    );
  }
</script>

<div
  class="chart-container"
  bind:offsetWidth={width}
  bind:offsetHeight={height}
>
  {#if width && height}
    <!-- Text annotation -->
    {#if hoveredData}
      <div
        class="tooltip-text"
        style="transform: translate({dimensions.marginLeft +
          hoveredData.position[0]}px, {dimensions.marginTop +
          dimensions.boundedHeight / 2 -
          mapHeight / 2 +
          hoveredData.position[1] +
          5}px);"
      >
        {hoveredData.get(selectedYear)["Country Name"]}
      </div>
    {/if}

    <!-- Scrubber -->
    <Scrubber
      bind:value={selectedYear}
      values={range(1997, 2021)}
      delay={500}
      autoplay={false}
      loop={false}
      transform={`translate(${dimensions.marginLeft}px, ${
        dimensions.marginTop
      }px)`}
    />

    <svg width={dimensions.width} height={dimensions.height} overflow="visible">
      <!-- Gradient -->
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={0}
          y1={lengthScale(50)}
          x2={0}
          y2={-lengthScale(50)}
        >
          {#each ticks(0, 1, 10) as t}
            <stop offset={t} stop-color={colorScale.interpolator()(t)} />
          {/each}
        </linearGradient>
        <linearGradient
          id={gradientId + "alt"}
          gradientUnits="userSpaceOnUse"
          x1={0}
          y1={0}
          x2={0}
          y2={-lengthScale(100)}
        >
          {#each ticks(0, 1, 10) as t}
            <stop offset={t} stop-color={colorScale.interpolator()(t)} />
          {/each}
        </linearGradient>
      </defs>

      <g
        transform={`translate(${dimensions.marginLeft}, ${
          dimensions.marginTop + dimensions.boundedHeight / 2 - mapHeight / 2
        })`}
      >
        <!-- Geo features -->
        <g class="geo-features">
          {#each features.features as f, i}
            <path transition:fade={{ duration: 1500 }} d={pathGenerator(f)} />
          {/each}
        </g>

        <!-- Spikes -->
        <!-- TODO: Gradient for the spikes -->
        <!-- TODO: On hover, show gradient area chart -->
        <g class="spikes">
          {#each dataRender as [id, d]}
            <path
              transform={`translate(${positionAccessor(d)})`}
              d={spike(lengthAccessorScaled(d.get(selectedYear)))}
              fill={colorAccessorScaled(d.get(selectedYear))}
              stroke={colorAccessorScaled(d.get(selectedYear))}
            />
          {/each}
        </g>

        <!-- Small multiples -->
        {#if hoveredData}
          <g
            transition:fade={{ duration: 400 }}
            class="multiple"
            transform={`translate(${[
              hoveredData.position[0] -
                xAccessorScaled(hoveredData.get(selectedYear)),
              hoveredData.position[1] - multipleSize,
            ]})`}
          >
            <!-- Gradient -->
            <!-- <linearGradient
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              x1={0}
              y1={lengthScale(100)}
              x2={0}
              y2={0}
            >
              {#each ticks(0, 1, 10) as t}
                <stop offset={t} stop-color={colorScale.interpolator()(t)} />
              {/each}
            </linearGradient> -->

            <!-- Frame -->
            <rect class="frame" width={multipleSize} height={multipleSize} />
            <!-- Area -->
            <path
              class="area"
              d={areaGenerator([...hoveredData.values()])}
              fill={`url(#${gradientId})`}
            />

            <!-- Line -->
            <!-- <path class="line" d={lineGenerator([...hoveredData.values()])} /> -->

            <!-- Guide -->
            <!-- <line
              class="guide"
              x1={xAccessorScaled(hoveredData.get(selectedYear))}
              y1={yAccessorScaled(hoveredData.get(selectedYear))}
              x2={xAccessorScaled(hoveredData.get(selectedYear))}
              y2={multipleSize}
              stroke={"white"}
              stroke-width={2}
            /> -->
            <path
              class="guide"
              d={lineGenerator([
                {
                  Year: selectedYear,
                  Value: yScale.invert(multipleSize),
                },
                {
                  Year: selectedYear,
                  Value: yAccessor(hoveredData.get(selectedYear)),
                },
              ])}
              stroke={"white"}
              stroke-width={2}
            />
          </g>
        {/if}

        <!-- svelte-ignore a11y-mouse-events-have-key-events -->
        <rect
          on:mousemove={(e) => {
            const bounds = e.target.getBoundingClientRect();
            const x = e.clientX - bounds.left;
            const y = e.clientY - bounds.top;

            // // Find closest data point
            const hoveredData = least(dataRender, ([id, d]) => {
              const [dx, dy] = positionAccessor(d);
              return Math.hypot(dx - x, dy - y);
            });

            hoveredId = hoveredData[0];
            // // HACK
            // hoveredKey =
            //   keyAccessor(hoveredData) === "World (Estimate)"
            //     ? "World"
            //     : keyAccessor(hoveredData);
          }}
          on:mouseout={() => {
            hoveredId = null;
          }}
          on:click={(e) => {
            // const bounds = e.target.getBoundingClientRect();
            // const x = e.clientX - bounds.left;
            // const y = e.clientY - bounds.top;
            // const selectedData = least(dataAll, (d) => {
            //   return Math.hypot(xAccessorScaled(d) - x, yAccessorScaled(d) - y);
            // });
            // selectedKey = keyAccessor(selectedData);
          }}
          width={dimensions.boundedWidth}
          height={mapHeight}
          fill-opacity="0"
        />
      </g>
    </svg>
  {/if}
</div>

<style>
  .chart-container {
    position: relative;
    height: 80vh;
    max-width: 100%;
  }
  .tooltip-text {
    transition: all 0.3s ease-out;
    position: absolute;
    font-weight: 700;
    text-shadow: var(--white-text-shadow);
    pointer-events: none;
  }
  .multiple {
    transition: all 0.3s ease-out;
  }
  .multiple .frame {
    stroke: var(--light-grey);
    /* stroke: none; */
    fill: var(--light-grey);
    /* fill-opacity: 0; */
    stroke-width: 2;
  }
  /* .multiple .line {
    transition: all 0.3s ease-out;
    fill: none;
    stroke: none;
  } */
  .multiple .area {
    transition: all 0.3s ease-out;
  }
  .multiple .guide {
    transition: all 0.3s ease-out;
  }
  .geo-features {
    fill: var(--light-orange);
    stroke: white;
    /* stroke: var(--light-grey); */
  }
  .spikes path {
    transition: d 0.3s ease-out;
    fill-opacity: 0.7;
  }
</style>
